import styled from 'styled-components';
import { Checkbox, Input, type CheckboxProps } from 'semantic-ui-react';
import { Check } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useSidebar } from '../contexts/sidebar';
import {
  useDesktopMediaQuery,
  useSmallScreenAndBelowMediaQuery,
  useTabletAndBelowMediaQuery,
} from '../layouts/ResponsiveMedia';

// ------------------------
// Text Elements
// ------------------------

export const HeaderText = styled.span`
  color: ${({ theme }) => theme.colors.black};
  font-weight: bold;
  font-size: 20px;
  line-height: 23px;
  white-space: nowrap;
`;

export const PageTitle = styled.span`
  color: ${({ theme }) => theme.colors.black};
  font-weight: bold;
  font-size: 20px;
  line-height: 23px;
  white-space: nowrap;
`;

export const TitleBarTitle = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-size: 15px;
  line-height: 23px;
  white-space: nowrap;
`;

export const MuteText = styled.span`
  color: ${({ theme }) => theme.colors.grey};
`;

export const MuteLink = styled(Link)`
  color: ${({ theme }) => theme.colors.grey};
  text-decoration: underline;
  &:hover {
    color: ${({ theme }) => theme.colors.grey};
    text-decoration: none;
  }
`;

interface IColorTextProps {
  color?: string;
}

export const ColorText = styled.span<IColorTextProps>`
  color: ${({ color }) => color || 'black'};
`;

export const PrimaryText = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

export const DangerText = styled.span`
  color: ${({ theme }) => theme.colors.danger};
`;

export const YellowText = styled.span`
  color: ${({ theme }) => theme.colors.yellow};
`;

export const SuccessText = styled.span`
  color: ${({ theme }) => theme.colors.success};
`;

export const ActiveText = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

export const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.accent};
`;

export const BoldText = styled.span`
  font-weight: bold;
`;

// ------------------------
// Dividers & Containers
// ------------------------

export const Border = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.primary} !important;
`;

export const MuteBorder = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.box} !important;
`;

export const ShadowBox = styled.div`
  background: ${({ theme }) => theme.colors.white} !important;
  box-shadow: 0 2px 15px rgba(57, 57, 72, 0.05);
  border-radius: 5px;
  padding: 16px;
`;

export const BodyContainer = styled.div`
  padding: 0 8px;
`;

// ------------------------
// Flex Layouts
// ------------------------

export const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;

export const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// ------------------------
// Header Bars
// ------------------------

interface IHeaderBarProps {
  children?: ReactNode;
  className?: string;
  zIndex?: number;
  darkBackground?: boolean;
  [key: string]: any;
}

const CommonStyleHeadeBar = styled.div<{ zIndex?: number }>`
  border-bottom: 0.5px solid;
  border-color: ${({ theme }) => theme.colors.grey};
  position: fixed;
  top: 0;
  z-index: ${({ zIndex }) => zIndex || 997};
  transition: all 0.2s;
  background: ${({ theme }) => theme.colors.box};
  height: 67px;
`;

const StyledHeaderBar = styled(CommonStyleHeadeBar)<{ isCollapsed?: boolean }>`
  padding: 14px 5px;
  width: calc(100% - ${({ isCollapsed }) => (isCollapsed ? '70px' : '255px')});
  left: ${({ isCollapsed }) => (isCollapsed ? '60px' : '245px')};
  top: 40px;
  &.is-mobile {
    top: 0px;
    left: 0px;
    width: 100%;
  }

  border-radius: 10px 10px 0 0;

  &.tab-layout {
    border-bottom: 0px;
  }

  &.no-sidebar {
    top: 0px;
    width: 100%;
    left: 0px;
    padding: 14px 100px;
  }
`;

export function HeaderBar({
  children,
  className = '',
  zIndex,
  darkBackground,
  ...rest
}: IHeaderBarProps) {
  const { isCollapsed } = useSidebar();
  const isTabletAndBelow = useSmallScreenAndBelowMediaQuery();
  const isMobile = useTabletAndBelowMediaQuery();

  return (
    <StyledHeaderBar
      zIndex={zIndex}
      className={`${className} ${isMobile ? 'is-mobile' : ''}`}
      isCollapsed={isCollapsed || isTabletAndBelow}
      {...rest}
    >
      {children}
    </StyledHeaderBar>
  );
}

// ------------------------
// Title Header
// ------------------------

const TitleBar = styled.div<{ zIndex?: number; $isDesktop?: boolean }>`
  position: fixed;
  top: 0;
  z-index: ${({ zIndex }) => zIndex || 997};
  transition: all 0.2s;
  height: 40px;
  width: calc(100vw - 125px);
  left: 50px;
  padding: 5px 0;
  .icon {
    min-width: 20px;
  }

  ${({ $isDesktop }) =>
    $isDesktop &&
    `
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    height: auto;
    min-height: 40px;
    z-index: 1;
    margin-bottom: 12px;
  `}
`;

export function TitleHeaderBar({
  children,
  className = '',
  zIndex,
  darkBackground,
  ...rest
}: IHeaderBarProps) {
  const isDesktop = useDesktopMediaQuery();
  const isMobile = useTabletAndBelowMediaQuery();

  return (
    <TitleBar
      zIndex={zIndex}
      $isDesktop={isDesktop}
      className={`${className} ${isMobile ? 'is-mobile' : ''}`}
      {...rest}
    >
      {children}
    </TitleBar>
  );
}

// ------------------------
// Checkboxes & Inputs
// ------------------------

const StyledSlider = styled(Checkbox)`
  &&& {
    input ~ label::before,
    input:checked ~ label::before,
    input:focus:checked ~ label::before {
      background-color: ${({ theme }) => theme.colors.green} !important;
    }
  }
`;

export const StyledSliderCheckbox = (props: CheckboxProps) => (
  <StyledSlider toggle {...props} />
);

const NormalStyledSlider = styled(Checkbox)`
  &&& {
    input:checked ~ label::before,
    input:focus:checked ~ label::before {
      background-color: ${({ theme }) => theme.colors.green} !important;
    }
  }
`;

export const NormalSliderCheckbox = (props: CheckboxProps) => (
  <NormalStyledSlider toggle {...props} />
);

const CheckboxHolder = styled.div`
  position: relative;
  display: flex;
`;

interface IPrimaryCheckboxProps extends CheckboxProps {
  width?: string;
  height?: string;
  rounded?: boolean;
  bgColor?: string;
}

const PrimaryCheckbox = styled(Checkbox)<IPrimaryCheckboxProps>`
  label:before,
  label:after {
    width: ${({ width }) => width ?? '14px'} !important;
    height: ${({ height }) => height ?? '14px'} !important;
  }

  &&& {
    input:checked ~ label::before {
      outline: none;
      border-radius: ${({ rounded }) => (rounded ? '50%' : '')};
      background: ${({ theme, bgColor }) => bgColor || theme.colors.primary};
      border-color: ${({ theme, bgColor }) => bgColor || theme.colors.primary};
    }
  }
`;

const CheckboxIcon = styled(Check)<{ top?: string; left?: string }>`
  position: absolute;
  pointer-events: none;
  top: ${({ top }) => top ?? '1px'};
  left: ${({ left }) => left ?? '1px'};
  z-index: 0;
`;

export function StyledCheckbox({
  checked,
  indeterminate,
  ...props
}: IPrimaryCheckboxProps) {
  const showCheckIcon = Boolean(checked) && !indeterminate;

  return (
    <CheckboxHolder>
      <PrimaryCheckbox checked={checked} indeterminate={indeterminate} {...props} />
      {showCheckIcon ? <CheckboxIcon size={12} color="white" weight="bold" /> : null}
    </CheckboxHolder>
  );
}

export const StyledInput = styled(Input)`
  &.similar-height {
    height: 40px;
  }
  input {
    border-color: ${({ theme }) => theme.colors.grey} !important;
  }
`;

export const CalenderInput = styled(StyledInput)`
  .icon {
    cursor: default;
    position: absolute;
    top: 6px;
    right: 5px;
    opacity: 0.5;
    pointer-events: none;
  }
  &:focus-within .icon {
    opacity: 0.9 !important;
  }
`;

// ------------------------
// Secondary Header
// ------------------------

export const SecondaryHeaderContainer = styled.div`
  margin: 0 -20px;
  padding: 10px 15px;
  position: fixed;
  top: 35px;
  right: 30px;
  z-index: 998;

  &.dark-bg {
    background-color: ${({ theme }) => theme.colors.box};
    width: calc(100vw - 70px);
    display: flex;
    justify-content: flex-end;
    border-radius: 10px 10px 0 0;
    top: 40px;
  }

  &.full-width {
    width: 100vw;
    left: 0;
    margin: 0;
    border-radius: 0;
  }
`;
